import styled from '@emotion/styled'
import PropTypes from '~/lib/propTypes'
import Link from 'next/link'
import { AUTHENTICATION_VIEWED, SELL_STARTED } from '~/lib/tracking-events'
import AuthDialog from '~/components/AuthDialog'
import { color, space, device, fontSize, radius, Button, Pill } from '@ticketswap/solar'
import { track } from '~/utils/analytics'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import BecomePartnerButton from '~/components/BecomePartnerButton'
import {
  ChevronRightAlt,
  Ticket,
  List,
  Alert,
  Avatar,
  Chat,
  Logout,
  TicketSwap,
  Dot,
  Bookmark,
} from '@ticketswap/solar/icons'
import { forwardRef } from 'react'
import { useTranslation } from '~/components/TranslationContext'
import { RouteMap } from '~/lib/routeMap'
import { useQuery } from '@apollo/client'
import { GET_LOGGED_IN_USER_KYC_STATUS } from '~/graphql/queries/getLoggedInUserKYCStatus'
import { useIntercom } from '~/components/IntercomProvider'

const StyledButton = styled.button`
  font-family: inherit;
  font-size: ${fontSize[18]};
  text-align: center;
  width: 100%;
  cursor: pointer;
  border: none;
  padding-block: 0;
  padding-inline: ${space[16]};
  height: ${space[56]};
  display: inline-flex;
  min-width: 256px;
  justify-content: space-between;
  align-items: center;
  text-decoration: none;
  line-height: 1;
  color: ${color.foreground};
  background-color: ${color.elevatedBackground};
  border-radius: ${radius.md};
  white-space: nowrap;

  &:hover,
  &:focus {
    color: ${color.foreground};
    background-color: ${color.elevatedBackground};
  }

  &:active,
  &:focus {
    outline: none;
  }

  @media ${device.tablet} {
    padding-block: 0;
    padding-inline: ${space[8]};
    background-color: ${color.background};
  }
`

const ActionContainer = styled.div`
  @media ${device.tablet} {
    display: none;
  }
`

const Adornment = styled.span`
  transform: translateX(${space[8]});

  @media ${device.tablet} {
    display: none;
  }
`

const Label = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
`

const Badge = styled.span`
  background-color: ${props => (props.bgColor ? color[props.bgColor] ?? props.bgColor : 'white')};
  color: ${props => (props.theme === 'dark' ? 'white' : color.foregroundMuted)};
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: ${space[32]};
  height: ${space[32]};
  border-radius: ${space[16]};
  margin-inline-end: ${space[16]};

  @media ${device.tablet} {
    background-color: ${props => (props.bgColor ? color[props.bgColor] : color.elevatedBackground)};
    color: ${props => (props.theme === 'dark' ? 'white' : color.foregroundMuted)};
  }
`

const SyledTicketSwap = styled(TicketSwap)`
  color: ${color.invertedForeground};

  [data-theme='dark'] & {
    color: ${color.earth};
  }
`

export const MenuButton = forwardRef(({ children, onClick, on, onToggle, href }, ref) => (
  <StyledButton as="a" href={href} ref={ref} onClick={onClick} on={on} onToggle={onToggle}>
    {children}
    <Adornment>
      <ChevronRightAlt size={24} className="arrow" />
    </Adornment>
  </StyledButton>
))

MenuButton.displayName = 'MenuButton'

const Container = styled.div`
  display: grid;
  grid-gap: ${space[8]};

  @media ${device.tablet} {
    grid-gap: 0;
  }
`

const StyledPill = styled(Pill)`
  margin-inline-start: ${space[8]};
  min-width: 25px;
  justify-content: center;

  @media ${device.tablet} {
    margin-inline-start: unset;
    position: absolute;
    inset-inline-end: ${space[24]};
  }
`

const Menu = ({ type }) => {
  const { t } = useTranslation()
  const { show: showIntercom } = useIntercom()
  const { loggedInUser } = useLoggedInUser()
  const { notificationInboxMessages = {} } = loggedInUser || {}
  const { unreadMessageCount = 0 } = notificationInboxMessages

  const { data = {} } = useQuery(GET_LOGGED_IN_USER_KYC_STATUS)
  const { loggedInUser: kycUser } = data
  const { knowYourCustomer } = kycUser || {}
  const needsVerification = knowYourCustomer?.status === 'ACTION_REQUIRED'

  return (
    <Container>
      <ActionContainer>
        {type === 'partner' ? (
          <BecomePartnerButton type="light" fullWidth t={t} />
        ) : (
          <Link prefetch={false} href="/sell" passHref>
            <Button
              as="a"
              variant="success"
              fullWidth
              onClick={() => {
                track(SELL_STARTED, {
                  source: 'menu header',
                })
              }}
            >
              {t('common:menubar.sellTickets')}
            </Button>
          </Link>
        )}
      </ActionContainer>

      {type === 'partner' && (
        <>
          <Link prefetch={false} href={RouteMap['BLOG']} passHref>
            <MenuButton>{t('common:generic.blog')}</MenuButton>
          </Link>

          <Link prefetch={false} href="/how-does-it-work" passHref>
            <MenuButton>{t('common:menubar.howItWorks')}</MenuButton>
          </Link>
        </>
      )}

      {!loggedInUser && (
        <AuthDialog onToggle={on => on && track(AUTHENTICATION_VIEWED)}>
          {({ getToggleProps }) => (
            <>
              <MenuButton {...getToggleProps()}>{t('common:menubar.login')}</MenuButton>
              <Link prefetch={false} href={RouteMap['HOW_DOES_IT_WORK']} passHref>
                <MenuButton>{t('common:menubar.howItWorks')}</MenuButton>
              </Link>
              <Link prefetch={false} href={RouteMap['PARTNERS']} passHref>
                <MenuButton>{t('common:footer.primary.partners')}</MenuButton>
              </Link>
              <MenuButton onClick={showIntercom}>{t('common:menubar.help')}</MenuButton>
            </>
          )}
        </AuthDialog>
      )}

      {loggedInUser && (
        <>
          <Link prefetch={false} href={RouteMap['MY_TICKETS']} passHref>
            <MenuButton>
              <Label>
                <Badge bgColor="earth" theme="dark">
                  <Ticket size={20} />
                </Badge>
                {t('common:menubar.myTickets')}
              </Label>
            </MenuButton>
          </Link>

          <Link prefetch={false} href={RouteMap['MY_LISTINGS']} passHref>
            <MenuButton>
              <Label>
                <Badge bgColor="titan" theme="dark">
                  <List size={20} />
                </Badge>
                {t('common:menubar.myListings')}
              </Label>
            </MenuButton>
          </Link>

          <Link prefetch={false} href={RouteMap['TICKET_ALERTS']} passHref>
            <MenuButton>
              <Label>
                <Badge bgColor="sun" theme="dark">
                  <Alert size={20} />
                </Badge>
                {t('common:menubar.myWantedListings')}
              </Label>
            </MenuButton>
          </Link>

          <Link prefetch={false} href={RouteMap['FAVORITE_EVENTS']} passHref>
            <MenuButton>
              <Label>
                <Badge bgColor="#fe6e6d" theme="dark">
                  <Bookmark size={16} />
                </Badge>
                {t('common:menubar.favoriteEvents')}
              </Label>
            </MenuButton>
          </Link>

          {loggedInUser.isPartner && (
            <Link prefetch={false} href={RouteMap['PARTNER_DASHBOARD']} passHref>
              <MenuButton>
                <Label>
                  <Badge bgColor="foreground" theme="dark">
                    <SyledTicketSwap size={20} />
                  </Badge>
                  {t('common:menubar.partnerDashboard')}
                </Label>
              </MenuButton>
            </Link>
          )}

          <Link prefetch={false} href={RouteMap['PROFILE']} passHref>
            <MenuButton>
              <Label>
                <Badge theme="light">
                  <Avatar size={20} />
                </Badge>
                {t('common:generic.account')}

                {needsVerification && <StyledPill variant="danger">!</StyledPill>}
              </Label>
            </MenuButton>
          </Link>

          <Link
            prefetch={false}
            href={{
              pathname: RouteMap['NOTIFICATIONS'],
            }}
            passHref
          >
            <MenuButton>
              <Label>
                <Badge theme="light">
                  <Dot size={20} />
                </Badge>

                {t('common:generic.notifications')}

                <StyledPill variant={unreadMessageCount > 0 ? 'danger' : 'disabled'}>
                  {unreadMessageCount}
                </StyledPill>
              </Label>
            </MenuButton>
          </Link>

          <Link prefetch={false} href={RouteMap['CONVERSATIONS']} passHref>
            <MenuButton>
              <Label>
                <Badge theme="light">
                  <Chat size={20} />
                </Badge>
                {t('common:menubar.myConversations')}
              </Label>
            </MenuButton>
          </Link>

          {loggedInUser.isEmployee && (
            <MenuButton href={process.env.NEXT_PUBLIC_ADMIN_URL}>
              <Label>
                <Badge theme="light">
                  <TicketSwap size={20} />
                </Badge>
                {t('common:menubar.admin')}
              </Label>
            </MenuButton>
          )}

          <Link prefetch={false} href={RouteMap['LOGOUT']} passHref>
            <MenuButton>
              <Label>
                <Badge theme="light">
                  <Logout size={20} />
                </Badge>
                {t('common:menubar.logout')}
              </Label>
            </MenuButton>
          </Link>
        </>
      )}
    </Container>
  )
}

Menu.propTypes = {
  type: PropTypes.string,
}

export default Menu
